/**
 * 달력 월간/주간 뷰 전환 및 주간 스트림 계산용 순수 로직.
 * 주 인덱스 = 기준 일요일(2000-01-02, UTC)로부터의 경과 주 수 (일요일 시작 주).
 * DST 영향을 받지 않도록 내부 계산은 UTC 기반, 입출력은 "YYYY-MM-DD" 로컬 date key.
 */

export type CalendarViewMode = 'month' | 'week';

export const CALENDAR_VIEW_STORAGE_KEY = 'schedules.calendarView';

const WEEK_EPOCH_UTC = Date.UTC(2000, 0, 2); // 일요일
const MS_PER_DAY = 86_400_000;
const MS_PER_WEEK = 7 * MS_PER_DAY;

const pad2 = (n: number) => String(n).padStart(2, '0');

function utcTimeToDateKey(t: number): string {
  const d = new Date(t);
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
}

function dateKeyToUtcTime(dateKey: string): number {
  const [y, m, d] = dateKey.split('-').map(Number);
  return Date.UTC(y, m - 1, d);
}

/** "YYYY-MM-DD" → 주 인덱스 (해당 날짜가 속한 일요일 시작 주) */
export function dateKeyToWeekIndex(dateKey: string): number {
  return Math.floor((dateKeyToUtcTime(dateKey) - WEEK_EPOCH_UTC) / MS_PER_WEEK);
}

/** 주 인덱스 → 그 주 일요일의 "YYYY-MM-DD" */
export function weekIndexToSundayKey(weekIndex: number): string {
  return utcTimeToDateKey(WEEK_EPOCH_UTC + weekIndex * MS_PER_WEEK);
}

/** 주 인덱스 → 일~토 7일의 date key 배열 */
export function weekDates(weekIndex: number): string[] {
  const sunday = WEEK_EPOCH_UTC + weekIndex * MS_PER_WEEK;
  return Array.from({ length: 7 }, (_, i) => utcTimeToDateKey(sunday + i * MS_PER_DAY));
}

/**
 * 주가 "속한" 달 = 7일 중 4일 이상을 차지하는 달.
 * 일요일 시작 주에서는 수요일(4번째 날)이 속한 달이 항상 4일 이상을 차지한다.
 */
export function dominantMonthOfWeek(weekIndex: number): { year: number; month: number } {
  const wed = new Date(WEEK_EPOCH_UTC + weekIndex * MS_PER_WEEK + 3 * MS_PER_DAY);
  return { year: wed.getUTCFullYear(), month: wed.getUTCMonth() }; // month는 0-indexed
}

/**
 * 주 범위(양끝 포함)가 걸치는 모든 달 + 앞뒤 프리페치 달을 "YYYY-MM" 목록으로.
 * 서버 API가 월 단위이므로 주간 뷰 fetch 대상 계산에 사용.
 */
export function monthsForWeekRange(
  startWeek: number,
  endWeek: number,
  prefetchMonths = 1,
): string[] {
  const first = new Date(WEEK_EPOCH_UTC + startWeek * MS_PER_WEEK);
  const last = new Date(WEEK_EPOCH_UTC + endWeek * MS_PER_WEEK + 6 * MS_PER_DAY);
  // (year*12 + month) 선형 인덱스로 순회
  const fromIdx = first.getUTCFullYear() * 12 + first.getUTCMonth() - prefetchMonths;
  const toIdx = last.getUTCFullYear() * 12 + last.getUTCMonth() + prefetchMonths;
  const months: string[] = [];
  for (let i = fromIdx; i <= toIdx; i++) {
    const y = Math.floor(i / 12);
    const m = ((i % 12) + 12) % 12;
    months.push(`${y}-${pad2(m + 1)}`);
  }
  return months;
}

/** 뷰 모드 해석: URL 파라미터 우선 → localStorage → 기본 'month' */
export function resolveCalendarView(
  urlParam: string | null,
  stored: string | null,
): CalendarViewMode {
  if (urlParam === 'month' || urlParam === 'week') return urlParam;
  if (stored === 'month' || stored === 'week') return stored;
  return 'month';
}

/**
 * 주간 뷰 초기 중심 날짜 결정: date 파라미터 > month 파라미터의 1일 > 오늘.
 * (기존 월간 딥링크 호환 — month만 있으면 그 달 1일이 속한 주를 중심으로)
 */
export function initialWeekAnchor(
  monthParam: string | null,
  dateParam: string | null,
  todayKey: string,
): string {
  if (dateParam && /^\d{4}-\d{2}-\d{2}$/.test(dateParam)) return dateParam;
  if (monthParam && /^\d{4}-\d{2}$/.test(monthParam)) return `${monthParam}-01`;
  return todayKey;
}

/** 스냅 대상 scrollTop = 가장 가까운 주 행 경계 */
export function nearestRowScrollTop(scrollTop: number, rowHeight: number): number {
  return Math.round(scrollTop / rowHeight) * rowHeight;
}

/** 달 패리티 (주간 뷰 월 경계 배경 톤 교차용): (year*12+month) % 2 */
export function monthParityOfDateKey(dateKey: string): 0 | 1 {
  const [y, m] = dateKey.split('-').map(Number);
  return ((y * 12 + (m - 1)) % 2) as 0 | 1;
}
